// Iter 159b — 🚌 Choix des places bus : tuiles voyageurs à glisser-déposer sur le plan.
// Règle cinéma : toutes les places de la fiche côte à côte ou l'une devant l'autre (un bloc).
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import SpamNotice from '../components/SpamNotice';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Modèle « Franjo » (Iter 325) : côté chauffeur (droite écran) continu, côté portes
// (gauche écran) en 2 blocs séparés par l'espace de la porte arrière. Legacy rows → uniforme.
const zonesOf = (b) => (b.cote_chauffeur
  ? { ch: b.cote_chauffeur, av: b.cote_porte_avant || 0, ar: b.cote_porte_arriere || 0 }
  : { ch: b.rows || 0, av: b.rows || 0, ar: 0 });
const backIndexOf = (b) => zonesOf(b).ch;
const seatX = (s, backIndex) => (s.row >= backIndex ? s.col : (s.col < 2 ? s.col : s.col + 1));

// Iter 318 — règles de groupe : binômes CÔTE À CÔTE obligatoires (sans traverser l'allée),
// une seule personne devant/derrière si le groupe est impair, le tout en un seul bloc.
// Des places uniquement les unes derrière les autres (colonne) ne sont pas valides.
const blocValide = (seats, b) => {
  const n = seats.length;
  if (n <= 1) return '';
  const bi = backIndexOf(b);
  const cl = seats.map(s => [s.row, seatX(s, bi)]);
  const cote = (a, b) => a[0] === b[0] && Math.abs(a[1] - b[1]) === 1;
  const devant = (a, b) => a[1] === b[1] && Math.abs(a[0] - b[0]) === 1;
  const allee = (a, b) => a[0] === b[0] && ((a[1] === 1 && b[1] === 3) || (a[1] === 3 && b[1] === 1));
  const vus = new Set([0]);
  const pile = [0];
  while (pile.length) {
    const i = pile.pop();
    for (let j = 0; j < n; j++) {
      if (!vus.has(j) && (cote(cl[i], cl[j]) || devant(cl[i], cl[j]) || allee(cl[i], cl[j]))) { vus.add(j); pile.push(j); }
    }
  }
  if (vus.size !== n) return "Vos places doivent former UN SEUL bloc : côte à côte, l'une devant l'autre, ou de part et d'autre de l'allée sur la même rangée.";
  const maxPaires = (idx) => {
    if (!idx.length) return 0;
    const [c, ...rest] = idx;
    let best = maxPaires(rest);
    rest.forEach(j => { if (cote(cl[c], cl[j])) best = Math.max(best, 1 + maxPaires(rest.filter(k => k !== j))); });
    return best;
  };
  const idx = cl.map((_, i) => i);
  const paires = maxPaires(idx);
  const sans = n - paires * 2;
  if (sans > 1) return "Dès 2 personnes, chacun doit être assis CÔTE À CÔTE avec un membre du groupe (ex. groupe de 4 : 2 places à gauche + 2 à droite). Des places uniquement les unes derrière les autres ne sont pas acceptées.";
  if (sans === 1) {
    const ok = idx.some(i => {
      const rest = idx.filter(j => j !== i);
      return maxPaires(rest) === paires && rest.some(j => cote(cl[i], cl[j]) || devant(cl[i], cl[j]));
    });
    if (!ok) return "La personne sans binôme doit être placée juste DEVANT ou DERRIÈRE le reste du groupe (pas de l'autre côté de l'allée).";
  }
  return '';
};

export default function MonBus() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [placements, setPlacements] = useState({}); // vi -> {planId, num}
  const [selVi, setSelVi] = useState(null);          // tuile sélectionnée (tap mobile)
  const [confirme, setConfirme] = useState(false);
  const [flash, setFlash] = useState('');
  const [busy, setBusy] = useState(false);
  const [bulle, setBulle] = useState('');
  const bulleTimer = useRef(null);
  // Iter 339 — règle voyageur SOLO : ami déclaré + compteur d'échecs
  const [ami, setAmi] = useState({ nom: '', prenom: '' });
  const [soloFails, setSoloFails] = useState(0);
  const [trajet, setTrajet] = useState(() => { try { return new URLSearchParams(window.location.search).get('trajet') || ''; } catch { return ''; } }); // Iter 669/817 — trajet (destination du car) affiché, pré-sélectionnable via ?trajet=

  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/mon-bus/${token}${trajet ? `?trajet=${encodeURIComponent(trajet)}` : ''}`);
      setData(r.data);
      const init = {};
      (r.data.buses || []).forEach(b => b.seats.forEach(s => {
        if (s.statut === 'moi') init[s.vi] = { planId: b.id, num: s.num };
      }));
      setPlacements(init);
    } catch (e) {
      setError(e.response?.data?.detail || "Ce lien n'est pas valide ou a expiré. Contactez-nous au 06 60 41 62 18.");
    }
  }, [token, trajet]);

  useEffect(() => { load(); }, [load]);
  const changerTrajet = (d) => { setTrajet(d); setPlacements({}); setSelVi(null); setFlash(''); setConfirme(false); };

  if (error) return (
    <div className="min-h-screen bg-[#FAF7F2] flex items-center justify-center p-6">
      <div className="bg-white rounded-2xl shadow-lg p-8 max-w-md text-center" data-testid="bus-error">
        <div className="text-4xl mb-3">😕</div>
        <p className="text-[#1E3A5F] font-semibold">{error}</p>
      </div>
    </div>
  );
  if (!data) return (
    <div className="min-h-screen bg-[#FAF7F2] flex items-center justify-center">
      <p className="text-[#1E3A5F]">Chargement…</p>
    </div>
  );

  const readOnly = data.depart_passe;
  const valides = [];
  data.buses.forEach(b => b.seats.forEach(s => {
    if (s.statut === 'moi') valides.push(`${(data.voyageurs.find(v => v.index === s.vi)?.prenom || '')} → place ${s.num} (${b.name})`);
  }));
  const seatOwner = {}; // `${planId}:${num}` -> vi (placement local)
  Object.entries(placements).forEach(([vi, p]) => { if (p) seatOwner[`${p.planId}:${p.num}`] = Number(vi); });
  const nonPlaces = data.voyageurs.filter(v => !placements[v.index]);
  const planIds = new Set(Object.values(placements).filter(Boolean).map(p => p.planId));
  const tousPlaces = nonPlaces.length === 0 && data.voyageurs.length > 0;
  const memeBus = planIds.size <= 1;
  const bus = memeBus && planIds.size === 1 ? data.buses.find(b => b.id === [...planIds][0]) : null;
  const chosenSeats = bus ? bus.seats.filter(s => seatOwner[`${bus.id}:${s.num}`] !== undefined) : [];
  const erreurBloc = bus ? blocValide(chosenSeats, bus) : '';
  const connexe = !erreurBloc;
  // Iter 339 — règle SOLO : la place doit compléter une paire commencée (⭐),
  // sauf siège « réservé pour vous » 💛 ou ami déclaré (le voisin lui sera réservé)
  const soloCheck = (() => {
    if (!data.solo || readOnly) return { ok: true };
    const p = placements[data.voyageurs[0]?.index];
    if (!p) return { ok: true };
    const b = data.buses.find(x => x.id === p.planId);
    const seat = b?.seats.find(s => s.num === p.num);
    if (!b || !seat) return { ok: true };
    if (seat.statut === 'pour_vous') return { ok: true };
    const sp = b.solo_places || [];
    if (!sp.length || sp.includes(p.num)) return { ok: true };
    return { ok: false, places: sp.slice(0, 10) };
  })();
  const amiRempli = Boolean(ami.nom.trim() && ami.prenom.trim());
  const pret = tousPlaces && memeBus && connexe && (soloCheck.ok || amiRempli);

  const montrerBulle = (txt) => {
    setBulle(txt);
    clearTimeout(bulleTimer.current);
    bulleTimer.current = setTimeout(() => setBulle(''), 5500);
  };

  const placer = (planId, seat, vi) => {
    if (readOnly) return;
    if (seat.statut === 'pris' || seat.statut === 'ami_attendu') return;
    // Iter 323 — clic direct sur un numéro sans voyageur sélectionné :
    // on place automatiquement le prochain voyageur non placé (réflexe naturel des adhérents)
    if (vi === null || vi === undefined || Number.isNaN(vi)) {
      const suivant = nonPlaces[0];
      if (suivant) {
        setPlacements(p => ({ ...p, [suivant.index]: { planId, num: seat.num } }));
        const reste = nonPlaces.filter(v => v.index !== suivant.index);
        montrerBulle(reste.length
          ? `✅ ${suivant.prenom} → place ${seat.num}. Touchez une autre place pour ${reste[0].prenom} 👆`
          : `✅ ${suivant.prenom} → place ${seat.num}. Tout votre groupe est placé !`);
        return;
      }
      montrerBulle("👆 Tout le monde a déjà une place. Pour en changer : touchez d'abord la vignette du voyageur (en haut), puis sa nouvelle place.");
      return;
    }
    const owner = seatOwner[`${planId}:${seat.num}`];
    if (owner !== undefined && owner !== vi) return; // place déjà à un autre voyageur de la fiche
    setPlacements(p => ({ ...p, [vi]: { planId, num: seat.num } }));
    setSelVi(null);
  };

  const retirer = (vi) => setPlacements(p => ({ ...p, [vi]: null }));

  // Iter 319 — ✨ suggestion automatique du meilleur bloc conforme aux règles
  // Iter 323 — bouche les trous : un solo (ou le membre impair) prend en priorité
  // une place dont le voisin de binôme est déjà occupé, sans casser une paire libre.
  const chercherBloc = (libres, n, b) => {
    if (n <= 0 || libres.length < n) return null;
    const bi = backIndexOf(b);
    const bySeat = new Map(libres.map(s => [`${s.row}:${seatX(s, bi)}`, s]));
    const at = (y, x) => bySeat.get(`${y}:${x}`);
    const partX = (x) => (x === 0 ? 1 : x === 1 ? 0 : x === 3 ? 4 : 3); // partenaire de binôme
    if (n === 1) {
      const trou = (s) => {
        if (s.row >= bi) return false; // banquette arrière : pas de binôme strict
        return !at(s.row, partX(seatX(s, bi))); // partenaire déjà pris = trou à boucher
      };
      const tri = [...libres].sort((a, c) => a.num - c.num);
      return [tri.find(trou) || tri[0]];
    }
    const pairs = [];
    libres.forEach(s => {
      const x = seatX(s, bi);
      const d = at(s.row, x + 1);
      if (d) pairs.push([s, d]);
    });
    pairs.sort((p, q) => (p[0].row - q[0].row) || (p[0].num - q[0].num));
    const need = Math.floor(n / 2), single = n % 2;
    for (const anchor of pairs) {
      const chosen = [anchor];
      const used = new Set([anchor[0].num, anchor[1].num]);
      while (chosen.length < need) {
        let next = null;
        for (const [a, c] of chosen) {
          const xa = seatX(a, bi), xc = seatX(c, bi);
          const cands = [
            [[a.row + 1, xa], [c.row + 1, xc]],
            [[a.row - 1, xa], [c.row - 1, xc]],
            [[a.row, xa <= 1 ? 3 : 0], [a.row, xa <= 1 ? 4 : 1]],
          ];
          for (const [[y1, x1], [y2, x2]] of cands) {
            const s1 = at(y1, x1), s2 = at(y2, x2);
            if (s1 && s2 && !used.has(s1.num) && !used.has(s2.num)) { next = [s1, s2]; break; }
          }
          if (next) break;
        }
        if (!next) break;
        chosen.push(next);
        used.add(next[0].num); used.add(next[1].num);
      }
      if (chosen.length < need) continue;
      let flat = chosen.flat();
      if (single) {
        const cands = [];
        for (const s of flat) {
          const x = seatX(s, bi);
          [at(s.row + 1, x), at(s.row - 1, x)].forEach(e => {
            if (e && !used.has(e.num) && !cands.includes(e)) cands.push(e);
          });
        }
        // priorité aux places qui ne créent pas de trou (partenaire de binôme déjà pris ou dans le groupe)
        const sansTrou = cands.find(e => {
          if (e.row >= bi) return true;
          const p = at(e.row, partX(seatX(e, bi)));
          return !p || used.has(p.num);
        });
        const extra = sansTrou || cands[0];
        if (!extra) continue;
        flat = [...flat, extra];
      }
      if (blocValide(flat, b) === '') return flat.sort((a, c) => a.num - c.num);
    }
    return null;
  };

  const suggerer = () => {
    const n = data.voyageurs.length;
    // Iter 339 — solo : priorité au siège « réservé pour vous » 💛, puis aux places ⭐
    if (data.solo) {
      for (const b of data.buses) {
        const pv = b.seats.find(s => s.statut === 'pour_vous');
        const star = (b.solo_places || [])
          .map(num => b.seats.find(s => s.num === num && s.statut === 'libre')).filter(Boolean);
        const cible = pv || star[0];
        if (cible) {
          setPlacements({ [data.voyageurs[0].index]: { planId: b.id, num: cible.num } });
          setSelVi(null);
          return;
        }
      }
    }
    for (const b of data.buses) {
      const libres = b.seats.filter(s => s.statut !== 'pris' && s.statut !== 'ami_attendu');
      const bloc = chercherBloc(libres, n, b);
      if (bloc) {
        const np = {};
        data.voyageurs.forEach((v, i) => { np[v.index] = { planId: b.id, num: bloc[i].num }; });
        setPlacements(np);
        setSelVi(null);
        return;
      }
    }
    alert(data.buses.length > 1
      ? "Aucun bloc de places conforme aux règles n'est disponible automatiquement dans aucun des bus — choisissez vos places manuellement, ou contactez-nous au 06 60 41 62 18."
      : "Aucun bloc de places conforme aux règles n'est disponible automatiquement — choisissez vos places manuellement.");
  };


  const valider = async () => {
    setBusy(true);
    try {
      const body = {
        plan_id: [...planIds][0],
        confirme: true,
        ami_nom: data.solo ? ami.nom.trim() : '',
        ami_prenom: data.solo ? ami.prenom.trim() : '',
        placements: Object.entries(placements).filter(([, p]) => p)
          .map(([vi, p]) => ({ voyageur_index: Number(vi), seat_num: p.num })),
      };
      const r = await axios.post(`${API}/mon-bus/${token}/valider`, body);
      setFlash(`🎉 C'est validé dans le ${r.data.bus} (le ${r.data.horodatage}) : ${r.data.recap} — un email de confirmation vous a été envoyé.`);
      setConfirme(false);
      setSoloFails(0);
      await load();
    } catch (e) {
      setFlash('⚠️ ' + (e.response?.data?.detail || e.message));
      if (data.solo) setSoloFails(f => f + 1);
      await load();
    }
    setBusy(false);
  };

  const Tuile = ({ v }) => (
    <div
      draggable={!readOnly}
      onDragStart={e => e.dataTransfer.setData('vi', String(v.index))}
      onClick={() => setSelVi(selVi === v.index ? null : v.index)}
      data-testid={`bus-tuile-${v.index}`}
      style={{ backgroundColor: data.ma_couleur, opacity: selVi === v.index ? 1 : 0.85 }}
      className={`px-3 py-2 rounded-xl text-xs font-bold border-2 cursor-grab select-none transition text-white ${
        selVi === v.index ? 'ring-2 ring-[#1E3A5F] border-white scale-105' : 'border-transparent hover:opacity-100'}`}>
      👤 {v.prenom} {v.nom}{v.fiche && <span className="block text-[9px] font-normal opacity-90">fiche de {v.fiche}</span>}{v.bus_mode && <span className="block text-[9px] font-normal opacity-90" data-testid={`bus-mode-${v.index}`}>{v.bus_mode === 'aller_seul' ? '→ aller seulement (2 semaines)' : '← retour seulement (2 semaines)'}</span>}
    </div>
  );

  // Iter 504 (Kamel) — « Inscriptions du jour » (ex-« copines du jour », renommé Iter 593) : fiches inscrites ET payées le même jour → on se lie, l'une place tout le monde.
  const CopinesBox = () => {
    const [liste, setListe] = useState(null);
    const [sel, setSel] = useState(new Set());
    const [saving, setSaving] = useState(false);
    const charger = async () => {
      try { const r = await axios.get(`${API}/mon-bus/${token}/copines`); setListe(r.data.possibles || []); setSel(new Set((r.data.possibles || []).filter(x => x.liee).map(x => x.reg_id))); }
      catch { setListe([]); }
    };
    useEffect(() => { charger(); }, []); // eslint-disable-line react-hooks/exhaustive-deps
    const enregistrer = async () => {
      setSaving(true);
      try {
        await axios.post(`${API}/mon-bus/${token}/copines`, { reg_ids: Array.from(sel) });
        setFlash(sel.size ? `👯 ${sel.size} fiche(s) liée(s) — placez maintenant tout le monde côte à côte !` : 'Liaison retirée.');
        await load();
      } catch (e) { alert(e.response?.data?.detail || e.message); }
      setSaving(false);
    };
    if (readOnly || liste === null) return null;
    if (liste.length === 0 && !(data.copines || []).length) return null;
    return (
      <div className="bg-pink-50 border-2 border-pink-200 rounded-xl p-3" data-testid="bus-copines-box">
        <p className="text-xs font-bold text-pink-900">👥 Inscriptions du jour — vous voyagez avec des amis / de la famille inscrits sur une autre fiche ?</p>
        <p className="text-[11px] text-pink-800 mt-0.5">
          Voici les fiches de ce voyage <strong>inscrites et payées le même jour que vous</strong>. Cochez-les : leurs voyageurs
          apparaîtront avec les vôtres et vous pourrez <strong>choisir toutes les places ensemble</strong> (ils recevront la confirmation).
        </p>
        <div className="flex flex-wrap gap-2 mt-2">
          {liste.map(c => (
            <label key={c.reg_id} className={`flex items-center gap-1.5 text-xs px-2.5 py-1.5 rounded-lg border cursor-pointer ${sel.has(c.reg_id) ? 'bg-pink-600 text-white border-pink-600' : 'bg-white border-pink-300 text-pink-900'}`} data-testid={`bus-copine-${c.reg_id}`}>
              <input type="checkbox" className="accent-pink-600" checked={sel.has(c.reg_id)}
                onChange={e => setSel(prev => { const n = new Set(prev); if (e.target.checked) n.add(c.reg_id); else n.delete(c.reg_id); return n; })} />
              {c.label} ({c.nb} pers.)
            </label>
          ))}
          {liste.length === 0 && <span className="text-[11px] text-pink-700">Aucune autre fiche inscrite et payée le même jour que vous.</span>}
        </div>
        <button onClick={enregistrer} disabled={saving} className="mt-2 text-xs font-bold px-3 py-1.5 rounded-lg bg-pink-600 text-white disabled:opacity-50" data-testid="bus-copines-save">
          {saving ? '⏳…' : '👥 Se lier et placer ensemble'}
        </button>
      </div>
    );
  };

  const SeatMap = ({ b }) => {
    const { ch, av, ar } = zonesOf(b);
    const bi = ch;
    const rows = [];
    for (let r = bi; r >= 0; r--) {
      const seats = b.seats.filter(s => s.row === r);
      if (seats.length) rows.push({ r, seats });
    }
    return (
      <div className="inline-block bg-white border-2 border-gray-200 rounded-2xl p-4">
        <div className="text-center text-xs text-gray-400 mb-2 border-b pb-1">⬆️ ARRIÈRE du bus</div>
        {rows.map(({ r, seats }) => {
          const gapPorte = r < bi && r >= av && r < ch - ar; // côté gauche : espace de la porte arrière
          return (
            <div key={r} className="flex items-center gap-1.5 mb-1.5 justify-center">
              {gapPorte && (
                <>
                  <div className="h-9 rounded-lg border-2 border-dashed border-amber-400 bg-amber-50 flex items-center justify-center"
                    style={{ width: 78 }} data-testid={`bus-porte-arriere-${b.id}-${r}`}>
                    <span className="text-[8px] font-bold text-amber-600 leading-tight text-center">
                      {r === ch - ar - 1 ? <>🚪 PORTE<br />ARRIÈRE</> : '🚪'}
                    </span>
                  </div>
                  <div className="w-6" />
                </>
              )}
              {seats.map((s, i) => {
                const owner = seatOwner[`${b.id}:${s.num}`];
                const mine = owner !== undefined;
                const pourVous = s.statut === 'pour_vous';
                const amiAttendu = s.statut === 'ami_attendu';
                const prisAutre = s.statut === 'pris' || amiAttendu;
                const star = !readOnly && data.solo && !mine && s.statut === 'libre' && (b.solo_places || []).includes(s.num);
                return (
                  <React.Fragment key={s.num}>
                    {r < bi && s.col === 2 && i > 0 && <div className="w-6" />}
                    <button
                      disabled={readOnly || prisAutre}
                      onDragOver={e => !prisAutre && e.preventDefault()}
                      onDrop={e => { e.preventDefault(); placer(b.id, s, Number(e.dataTransfer.getData('vi'))); }}
                      onClick={() => mine ? retirer(owner) : placer(b.id, s, selVi)}
                      data-testid={`seat-${b.id}-${s.num}`}
                      title={mine ? `${data.voyageurs.find(v => v.index === owner)?.prenom} — cliquer pour retirer`
                        : amiAttendu ? `Réservée pour votre ami(e) ${data.ami_attendu ? `${data.ami_attendu.prenom} ${data.ami_attendu.nom}` : ''}`
                        : prisAutre ? 'Prise'
                        : pourVous ? 'Réservée pour VOUS par votre ami(e) — cliquez !'
                        : `Place ${s.num} libre${star ? ' — recommandée (à côté d\'un voyageur)' : ''}`}
                      style={mine ? { backgroundColor: data.ma_couleur }
                        : amiAttendu ? { backgroundColor: s.couleur || data.ma_couleur, opacity: 0.55 }
                        : prisAutre ? { backgroundColor: s.couleur || '#9ca3af', opacity: 0.7 } : undefined}
                      className={`w-9 h-9 rounded-lg text-[10px] font-bold transition leading-tight ${
                        mine ? 'text-white ring-2 ring-[#1E3A5F]'
                        : prisAutre ? 'text-white cursor-not-allowed'
                        : pourVous ? 'bg-yellow-300 text-yellow-900 border-2 border-yellow-500 cursor-pointer animate-pulse'
                        : `bg-white text-emerald-800 border-2 border-emerald-400 hover:bg-emerald-100 cursor-pointer${star ? ' ring-2 ring-amber-400' : ''}`}`}>
                      {mine ? (data.voyageurs.find(v => v.index === owner)?.prenom || '').slice(0, 5)
                        : amiAttendu ? '🤝' : pourVous ? '💛' : s.num}
                    </button>
                  </React.Fragment>
                );
              })}
            </div>
          );
        })}
        <div className="flex items-center justify-between gap-3 text-[10px] text-gray-400 mt-1 border-t pt-1.5">
          <span className="font-bold text-amber-600">🚪 Porte avant</span>
          <span className="font-bold">AVANT</span>
          <span>🧑‍✈️ Chauffeur</span>
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-[#FAF7F2] py-8 px-4">
      {bulle && (
        <div className="fixed bottom-5 inset-x-0 z-50 flex justify-center px-4 pointer-events-none" data-testid="bus-bulle">
          <p className="bg-[#1E3A5F] text-white text-sm font-bold rounded-2xl px-5 py-3 shadow-2xl max-w-md text-center border-2 border-[#79c9ea]">
            {bulle}
          </p>
        </div>
      )}
      <div className="max-w-4xl mx-auto">
        <div className="bg-[#1E3A5F] text-white rounded-t-2xl p-6">
          <p className="text-sm text-[#79c9ea] font-semibold">Rhône Alpes Amitié Voyages</p>
          <div className="bg-white rounded-xl px-4 py-2 inline-block mt-2" data-testid="monbus-logo-header">
            <img src="/monbus-logo.png" alt="MonBus" className="h-8 sm:h-10 w-auto" />
          </div>
          <p className="text-sm font-bold text-[#79c9ea] mt-2 italic">« Je choisis ma place, je voyage serein »</p>
          <p className="text-xs text-gray-300 mt-1">L'angoisse du voyage en moins 😌 — choisissez vos places tôt : <strong>première arrivée, première servie !</strong></p>
          <p className="text-sm mt-2 text-gray-200">Voyage : <strong>{data.voyage}</strong> ({data.dates})</p>
          {data.solo ? (
            <p className="text-xs mt-2 bg-white/10 rounded-lg px-3 py-2" data-testid="bus-regle-solo">
              🧍 <strong>Vous voyagez seul(e) sur cette fiche.</strong> Pour garder un bus bien rangé,
              choisissez une place <strong>à côté d'un voyageur déjà installé</strong> (places marquées ⭐).
              Vous souhaitez voyager à côté d'un(e) ami(e) ? Inscrivez-vous sur la <strong>même fiche</strong>{data.copines_possibles > 0 ? <>, ou <strong>liez-vous ci-dessous</strong> aux amis / à la famille inscrits et payés le même jour que vous (« Inscriptions du jour »)</> : <> — ou inscrivez-vous et payez <strong>le même jour</strong> : vous pourrez alors vous lier et choisir vos places ensemble</>}.
            </p>
          ) : (
          <p className="text-xs mt-2 bg-white/10 rounded-lg px-3 py-2">
            ⚡ <strong>Première arrivée, première servie.</strong> Dès 2 personnes, votre groupe doit être
            assis <strong>en binômes côte à côte</strong> (ex. groupe de 4 : 2 places à gauche + 2 à droite).
            Si le groupe est impair, une seule personne peut être juste devant ou derrière.
            Modifiable jusqu'au départ.
          </p>
          )}
          {!readOnly && (
            <p className="text-xs mt-2 bg-amber-500/20 border border-amber-300/40 rounded-lg px-3 py-2" data-testid="bus-warning-auto">
              ⏳ <strong>Important :</strong> sans choix de votre part avant le départ, une place vous sera attribuée
              automatiquement par les organisateurs — attribution <strong>définitive et non négociable</strong>.
            </p>
          )}
        </div>
        <div className="bg-white rounded-b-2xl shadow-lg p-6 space-y-5" data-testid="bus-form">
          {readOnly && (
            <p className="text-sm font-bold text-orange-700 bg-orange-50 border border-orange-200 rounded-lg p-3">
              Le départ est passé — le plan est en lecture seule.
            </p>
          )}
          {flash && (
            <p data-testid="bus-flash" className={`text-sm font-bold rounded-lg p-3 ${flash.startsWith('⚠️') ? 'bg-red-50 text-red-700 border border-red-200' : 'bg-emerald-50 text-emerald-800 border border-emerald-200'}`}>
              {flash}
            </p>
          )}
          {flash && /email de confirmation/.test(flash) && <SpamNotice quoi="l'e-mail de confirmation de vos places" compact testId="bus-spam-notice" />}
          {valides.length > 0 && !flash && (
            <p className="text-sm rounded-lg p-3 bg-emerald-50 text-emerald-800 border border-emerald-200" data-testid="bus-valides-recap">
              ✅ <strong>Vos places actuellement validées :</strong> {valides.join(', ')}.
              {!readOnly && ' Vous pouvez encore les modifier jusqu\'au départ.'}
            </p>
          )}
          {valides.length > 0 && (
            <div className="text-center bg-white border-2 border-dashed border-[#1E3A5F] rounded-xl p-4" data-testid="bus-qr-block">
              <p className="text-sm font-bold text-[#1E3A5F] mb-2">🎫 Votre QR code d'embarquement</p>
              <img src={`${API}/bus-qr/${token}.png`} alt="QR embarquement" width="150" height="150"
                className="mx-auto rounded-lg border" data-testid="bus-qr-img" />
              <p className="text-xs text-gray-500 mt-2">Présentez-le à l'accompagnateur en montant dans le bus
              (il vaut pour tout votre groupe) — il est aussi dans votre email de confirmation.</p>
            </div>
          )}
          {data.trajets && data.trajets.length > 1 && (
            <div className="bg-white border-2 border-[#79c9ea] rounded-xl p-2" data-testid="bus-trajets">
              <p className="text-[11px] font-bold text-[#1E3A5F] px-1 mb-1.5">🔁 Votre voyage comporte {data.trajets.length} trajets en car — ce sont des cars différents, choisissez vos places pour chacun :</p>
              {data.multi_semaines && (
                <p className="text-[11px] text-[#1E3A5F] px-1 mb-1.5" data-testid="bus-multi-semaines-info">📅 Séjour sur plusieurs semaines : le car <b>aller</b> est celui de votre première semaine, le car <b>retour</b> celui de votre dernière semaine.</p>
              )}
              <div className="flex gap-2 flex-wrap">
                {data.trajets.map(t => (
                  <button key={t.destination} onClick={() => changerTrajet(t.destination)} data-testid={`bus-trajet-${t.destination}`}
                    className={`flex-1 min-w-[140px] text-left rounded-lg px-3 py-2 border-2 transition ${t.actif ? 'border-[#1E3A5F] bg-[#1E3A5F] text-white' : 'border-gray-200 bg-gray-50 text-[#1E3A5F] hover:border-[#79c9ea]'}`}>
                    <span className="block text-xs font-bold">🚌 {t.label || `${data.voyage}`}</span>
                    <span className={`block text-[11px] ${t.actif ? 'text-sky-200' : 'text-gray-500'}`}>
                      {t.places >= data.trajet.nb_voyageurs ? `✅ ${t.places} place${t.places > 1 ? 's' : ''} choisie${t.places > 1 ? 's' : ''}` : t.places > 0 ? `⚠️ ${t.places}/${data.trajet.nb_voyageurs} placé(s)` : '⏳ places à choisir'}
                    </span>
                  </button>
                ))}
              </div>
            </div>
          )}
          {data.depart && (
            <div className="bg-blue-50 border border-blue-200 rounded-xl p-3 text-sm text-[#1E3A5F]" data-testid="bus-depart-info">
              {data.depart.retour_seul ? (
                <>🔁 <strong>Car de retour uniquement</strong> : pas de rendez-vous le matin, ce car vous ramène à la fin de votre voyage{data.depart.lieu ? <> (dépose : {data.depart.lieu})</> : null}.</>
              ) : data.depart.lieu ? (
                <>
                  📍 <strong>Votre départ :</strong> {data.depart.lieu}
                  {data.depart.horaire
                    ? <> — 🕒 <strong>Rendez-vous : {data.depart.horaire}</strong> <span className="text-xs text-gray-500">(heure française)</span></>
                    : <> — 🕒 <span className="text-gray-500">horaire précis confirmé avant le départ</span></>}
                </>
              ) : (
                <>
                  🕒 <strong>Horaires de départ :</strong>{' '}
                  {data.depart.tous.map((t, i) => (
                    <span key={i}>{i > 0 && ' · '}{t.lieu} — <strong>{t.horaire}</strong></span>
                  ))}
                </>
              )}
            </div>
          )}
          {!readOnly && (
            <div className="bg-blue-50/60 border-2 border-dashed border-[#79c9ea] rounded-xl p-3" data-testid="bus-tuiles-tray">
              <div className="flex items-center justify-between gap-2 flex-wrap mb-2">
                <p className="text-xs font-bold text-[#1E3A5F]">
                {nonPlaces.length > 0
                  ? `1️⃣ Glissez (ou touchez puis cliquez une place) chaque voyageur sur le plan — ${nonPlaces.length} à placer :`
                  : '✅ Tout le monde est placé ! Vérifiez puis validez ci-dessous.'}
                </p>
                <button onClick={suggerer} data-testid="bus-suggestion-btn"
                  className="text-xs font-bold px-3 py-2 rounded-lg bg-[#1E3A5F] text-white hover:bg-[#2a4d7a] transition">
                  ✨ Placer mon groupe automatiquement
                </button>
              </div>
              <div className="flex flex-wrap gap-2 min-h-[38px]">
                {nonPlaces.map(v => <Tuile key={v.index} v={v} />)}
              </div>
            </div>
          )}
          <CopinesBox />
          {false && (
            <div className="bg-purple-50 border-2 border-purple-200 rounded-xl p-3" data-testid="bus-ami-box">
              <p className="text-xs font-bold text-purple-900">🤝 Vous attendez un(e) ami(e) inscrit(e) sur un autre formulaire ?</p>
              <p className="text-[11px] text-purple-700 mt-0.5">
                Indiquez son prénom et son nom (tels qu'inscrits sur sa fiche) : vous pourrez alors vous installer
                sur une rangée libre et le siège à côté de vous lui sera <strong>automatiquement réservé</strong>.
              </p>
              {data.ami_attendu && (
                <p className="text-[11px] font-bold text-purple-800 mt-1 bg-purple-100 rounded px-2 py-1" data-testid="bus-ami-reserve-info">
                  💛 Le siège {data.ami_attendu.seat} ({data.ami_attendu.bus}) est déjà réservé pour {data.ami_attendu.prenom} {data.ami_attendu.nom} — il/elle le retrouvera en ouvrant son plan.
                </p>
              )}
              <div className="flex flex-wrap gap-2 mt-2">
                <input value={ami.prenom} onChange={e => setAmi(a => ({ ...a, prenom: e.target.value }))}
                  placeholder="Prénom de l'ami(e)" data-testid="bus-ami-prenom"
                  className="flex-1 min-w-[130px] border border-purple-300 rounded-lg px-2 py-1.5 text-xs" />
                <input value={ami.nom} onChange={e => setAmi(a => ({ ...a, nom: e.target.value }))}
                  placeholder="NOM de l'ami(e)" data-testid="bus-ami-nom"
                  className="flex-1 min-w-[130px] border border-purple-300 rounded-lg px-2 py-1.5 text-xs uppercase" />
              </div>
            </div>
          )}
          <div className="flex flex-wrap items-center gap-4 text-xs">
            <span className="flex items-center gap-1"><span className="w-4 h-4 rounded bg-white border-2 border-emerald-400 inline-block" /> Libre</span>
            {data.solo && !readOnly && (
              <span className="flex items-center gap-1"><span className="w-4 h-4 rounded bg-white border-2 border-emerald-400 ring-2 ring-amber-400 inline-block" /> ⭐ Recommandée (à côté d'un voyageur)</span>
            )}
            <span className="flex items-center gap-1"><span className="w-4 h-4 rounded inline-block" style={{ background: 'linear-gradient(135deg,#dc2626 50%,#2563eb 50%)', opacity: 0.7 }} /> Prise (1 couleur = 1 groupe)</span>
            <span className="flex items-center gap-1"><span className="w-4 h-4 rounded inline-block" style={{ backgroundColor: data.ma_couleur }} /> Votre groupe (cliquer pour retirer)</span>
            <button onClick={load} data-testid="bus-refresh-btn"
              className="ml-auto px-3 py-1.5 bg-gray-100 rounded-lg font-bold text-[#1E3A5F] hover:bg-gray-200 transition">
              🔄 Actualiser le plan
            </button>
          </div>
          {data.buses.length === 0 && (
            <p className="text-sm text-gray-500 italic">Le plan du bus n'est pas encore disponible — revenez bientôt !</p>
          )}
          <div className="flex flex-wrap gap-6 justify-center">
            {data.buses.map(b => {
              const nbGroupe = data.voyageurs.length;
              const trop = !readOnly && nbGroupe > 0 && b.libres < nbGroupe;
              const autre = data.buses.find(x => x.id !== b.id && x.libres >= nbGroupe);
              return (
                <div key={b.id} className="text-center">
                  <p className="font-bold text-[#1E3A5F] text-sm mb-2">🚌 {b.name} <span className="text-gray-400 font-normal">· {b.libres} libre{b.libres > 1 ? 's' : ''}</span></p>
                  {trop && (
                    <p className="text-[11px] font-bold text-red-700 bg-red-50 border border-red-200 rounded-lg px-2 py-1.5 mb-2 max-w-[290px] mx-auto"
                      data-testid={`bus-complet-${b.id}`}>
                      ⚠️ Plus assez de places dans ce bus pour votre groupe de {nbGroupe} ({b.libres} restante{b.libres > 1 ? 's' : ''}).{' '}
                      {autre ? <>Installez-vous dans le <strong>{autre.name}</strong> 👉</> : 'Contactez-nous au 06 60 41 62 18.'}
                    </p>
                  )}
                  <SeatMap b={b} />
                </div>
              );
            })}
          </div>
          {!readOnly && data.voyageurs.length > 0 && (
            <div className="text-center space-y-3">
              {tousPlaces && !memeBus && (
                <p className="text-xs font-bold text-red-600" data-testid="bus-err-bus">⚠️ Tout le groupe doit être dans le même bus.</p>
              )}
              {tousPlaces && memeBus && !connexe && (
                <p className="text-xs font-bold text-red-600" data-testid="bus-err-connexe">⚠️ {erreurBloc}</p>
              )}
              {tousPlaces && memeBus && connexe && !soloCheck.ok && !amiRempli && (
                <p className="text-xs font-bold text-amber-800 bg-amber-50 border border-amber-300 rounded-lg p-3 max-w-lg mx-auto" data-testid="bus-err-solo">
                  ⭐ Cette place ouvre une nouvelle rangée alors qu'il reste des places à côté d'autres voyageurs
                  ({(soloCheck.places || []).map(n => `n° ${n}`).join(', ')}). Choisissez l'une d'elles.
                </p>
              )}
              {soloFails >= 2 && (
                <p className="text-xs font-bold text-red-700 bg-red-50 border border-red-200 rounded-lg p-3 max-w-lg mx-auto" data-testid="bus-solo-aide">
                  😅 Plusieurs tentatives sans succès ? Contactez les organisateurs au <strong>06 60 41 62 18</strong> (WhatsApp) — nous vous placerons à la main.
                </p>
              )}
              {pret && (
                <label className="flex items-start gap-2 text-xs text-gray-700 max-w-lg mx-auto text-left bg-amber-50 border border-amber-200 rounded-lg p-3 cursor-pointer">
                  <input type="checkbox" checked={confirme} onChange={e => setConfirme(e.target.checked)}
                    data-testid="bus-confirme-checkbox" className="mt-0.5" />
                  <span><strong>Je confirme ce choix pour notre groupe.</strong> Règle « première arrivée, première
                  servie » : ce choix est horodaté, confirmé par email et <strong>fait foi</strong>. Je peux encore
                  le modifier jusqu'au départ (chaque modification est également confirmée par email).</span>
                </label>
              )}
              <button onClick={valider} disabled={!pret || !confirme || busy}
                data-testid="bus-valider-btn"
                className="px-8 py-3 bg-[#1E3A5F] text-white font-bold rounded-xl hover:bg-[#2a4d7a] disabled:opacity-40">
                {busy ? 'Validation…' : `✅ Valider nos ${data.voyageurs.length > 1 ? `${data.voyageurs.length} places` : 'place'}`}
              </button>
            </div>
          )}
          <p className="text-xs text-gray-400 text-center">
            Une question ? WhatsApp : 06 60 41 62 18 — Rhône Alpes Amitié Voyages
          </p>
        </div>
      </div>
    </div>
  );
}
